import qs from 'qs'
import { Logger } from '~/plugins/log'
const logger = new Logger()
logger.setContext('Checkout')

function normaliseString (str) {
  str = (str || '').toLowerCase()
  str = str.replace(/^,+|,+$/g, '')
  str = str.trim()
  return str
}

export const hashAddress = (address) => {
  if (!address || !Object.keys(address).length) return 0

  if (address.street) {
    if (Array.isArray(address.street)) {
      address.address1 = address.street ? address.street[0] : ''
      address.address2 = address.street.length > 1 ? address.street[1] : ''
    } else {
      address.address1 = address.street.split('\n')[0]
      address.address2 = address.street.includes('\n') ? address.street.split('\n')[1] : ''
    }
  }

  let concatenated = normaliseString(address.firstname)
  concatenated += normaliseString(address.lastname)
  concatenated += normaliseString(address.company)
  concatenated += normaliseString(address.address1)
  concatenated += normaliseString(address.address2)
  concatenated += normaliseString(address.city)
  concatenated += normaliseString(address.state)
  concatenated += normaliseString(address.postCode)
  concatenated += normaliseString(address.country)

  let hash = 0; let i; let chr
  if (concatenated.length === 0) return hash
  for (i = 0; i < concatenated.length; i++) {
    chr = concatenated.charCodeAt(i)
    hash = ((hash << 5) - hash) + chr
    hash |= 0 // Convert to 32bit integer
  }

  address.hash = hash
  return address
}

function getUniqueAddresses (addresses, phoneNumber) {
  addresses = addresses.map((address) => {
    try {
      if (typeof address === 'string') address = JSON.parse(address)
      if (typeof address !== 'object' || address === null) return false
      address.telephone = phoneNumber || address.telephone
      address.firstname = (address.name || '').split(' ')[0] || address.firstname
      address.lastname = (address.name || '').split(' ').slice(1).join(' ') || address.lastname
      return address
    } catch (error) {
      logger.error(error)
      this.app.$sentry.captureException(error)
    }
    return false
  }).filter(address => address).map(hashAddress)

  const uniqueAddresses = []
  addresses.forEach((address) => {
    if (uniqueAddresses.find(a2 => a2.hash === address.hash)) return
    uniqueAddresses.push(address)
  })
  return uniqueAddresses
}

const getDefaultState = () => ({
  addresses: [],
  phoneNumbers: {},
  billingAddress: {},
  shippingAddress: {},
  shippingMethods: [],
  shippingMethod: null,
  paymentMethods: [],
  paymentMethod: {},
  stepState: {},
  quote: {},
  items: [],
  coupons: [],
  showSignup: null,
  signupChoice: null
})

export const state = getDefaultState

export const mutations = {
  SET_ADDRESSES (state, { addresses, phoneNumber }) {
    state.addresses = getUniqueAddresses.bind(this)(addresses, phoneNumber)
  },
  ADD_ADDRESS (state, address) {
    if (!address || !address.street || !address.firstname) return
    
    // Don't add gift vouchers dummy address to list of addresses
    if (
      (address.company || '').toLowerCase().includes('gift vouchers') ||
      (address.lastname || '').toLowerCase().includes('gift vouchers')
    ) return
    
    state.addresses = getUniqueAddresses.bind(this)([address, ...(state.addresses || [])], address.telephone)
  },
  SET_PHONE_NUMBER (state, { email, phoneNumber }) {
    state.phoneNumbers = {
      ...state.phoneNumbers,
      [email]: phoneNumber
    }
  },
  CLEAR_SESSION (state) {
    state = getDefaultState()
  },
  SET_BILLING_ADDRESS (state, billingAddress) {
    state.billingAddress = billingAddress
  },
  SET_SHIPPING_ADDRESS (state, shippingAddress) {
    state.shippingAddress = shippingAddress
  },
  SET_QUOTE (state, quote) {
    state.quote = quote
  },
  SET_ITEMS (state, items) {
    state.items = items.filter(item => Object.keys(item || {}).length)
  },
  SET_COUPONS (state, coupons) {
    state.coupons = coupons
  },
  RESET (state) {
    Object.assign(state, getDefaultState())
  },
  SET_PAYMENT_METHODS (state, methods) {
    state.paymentMethods = methods || []
  },
  SET_PAYMENT_METHOD (state, paymentMethod) {
    state.paymentMethod = paymentMethod || {}
  },
  SET_SHIPPING_METHODS (state, shippingMethods) {
    state.shippingMethods = shippingMethods || []
  },
  SET_SHIPPING_METHOD (state, shippingMethod) {
    state.shippingMethod = shippingMethod || null
  },
  SET_STEP_STATE (state, stepState) {
    state.stepState = stepState || {}
  },
  SET_SHOW_SIGNUP (state, showSignup) {
    state.showSignup = showSignup
  },
  SET_SIGNUP_CHOICE (state, choice) {
    state.signupChoice = choice
  }
}

export const getters = {
  addresses: state => state.addresses || [],
  phoneNumber: state => email => state.phoneNumbers[email] || null,
  hasVerified: state => email => email in state.phoneNumbers,
  addressHash: state => address => hashAddress(address).hash,
  shippingAddress: state => state.shippingAddress || {},
  billingAddress: state => state.billingAddress || {},
  items: state => state.items || [],
  quote: state => state.quote || {},
  coupon: state => state.quote.coupon_code,
  coupons: state => state.coupons,
  hasFreeShipping: state => (state.coupons || []).some(coupon => coupon.free_shipping === '2'),
  grandTotal: state => parseFloat(state.quote.grand_total),
  subTotal: state => parseFloat(state.quote.subtotal),
  discount: state => parseFloat(state.quote.subtotal) - parseFloat(state.quote.subtotal_with_discount),
  noShippingAddressRequired: state => state.items.every(item => item.sku.includes('egiftcard-')),
  shippingMethods: state => state.shippingMethods,
  shippingMethod: (state, getters) => getters.shippingMethods.find(method => method.code && method.code === state.shippingMethod),
  paymentMethod: state => state.paymentMethod,
  paymentMethods: state => state.paymentMethods,
  stepState: state => step => state.stepState[step] || {},
  getAddressFromMagento: state => address => ({
    firstname: address.firstname,
    lastname: address.lastname,
    company: address.company,
    street: Array.isArray(address.street) ? address.street : (address.street || '').split('\n'),
    city: address.city,
    region: address.region,
    regionId: address.region_id,
    postcode: address.postcode,
    country: address.country_id,
    telephone: address.telephone
  }),
  showSignup: state => state.showSignup,
  signupChoice: state => state.signupChoice
}

export const actions = {
  async CHECK_EMAIL ({ commit, state }, { email, sendSMS }) {
    const { data } = await this.$publicAPI.quietGet(`checkout/validate-email/?email=${email}&send_sms=${sendSMS ? 'true' : 'false'}`)
    return data
  },

  async VERIFY_PIN ({ commit, rootGetters }, { email, pin, last4 }) {
    const { data } = await this.$publicAPI.post('checkout/verify-pin/', { email, pin, last4 })
    if (data.valid) {
      const addresses = data.addresses.map((address) => {
        if (typeof address === 'string') address = JSON.parse(address)

        const regions = rootGetters['store/regions'](address.country) || []
        const region = regions.find(region => (region.default_name || '').toLowerCase() === (address.region || '').toLowerCase()) || {}

        return {
          name: address.name,
          company: address.company,
          street: [address.address1, address.address2],
          city: address.city,
          region: address.state,
          region_id: region.region_id,
          postcode: address.postCode,
          country_id: address.country,
          telephone: address.telephone
        }
      })
      commit('SET_ADDRESSES', { addresses, phoneNumber: data.phone_number })
      commit('SET_PHONE_NUMBER', { email, phoneNumber: data.phone_number })
    }
    return data
  },

  async SAVE_SHIPPING ({ commit, state, rootState, rootGetters }, address) {
    logger.debug('SAVE_SHIPPING')

    const body = {
      'shipping-address': 'on',
      shipping: {
        address_id: null,
        middlename: null,
        fax: null,
        save_in_address_book: 0,
        ...address
      }
    }

    const regions = rootGetters['store/regions'](address.country)
    if (regions.length > 0 && address.regionId && regions.filter(region => region.region_id === address.regionId).length) {
      body.shipping.region_id = address.regionId
    }

    try {
      body.form_key = rootState.cart.formKey
      const { data } = await this.$ecomAPI.quietPost(
        'onepage/saveShipping/',
        qs.stringify(body, { arrayFormat: 'brackets' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.error || typeof (data) === 'string') {
        commit('cart/ADD_ERROR_MESSAGE', { body: data.error || data.message || 'Unable to save shipping address. Please try again.', args: data.message_params || {} }, { root: true })
        return false
      } else {
        commit('ADD_ADDRESS', address)
        commit('SET_SHIPPING_ADDRESS', data.address)
        commit('SET_SHIPPING_METHODS', data.shipping_methods)
        commit('SET_SHOW_SIGNUP', data.show_signup)
        commit('SET_QUOTE', { ...state.quote, customer_email: address.email })
        logger.debug('SAVE_SHIPPING succeeded')
        return data
      }
    } catch (error) {
      logger.debug('SAVE_SHIPPING failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      commit('cart/ADD_ERROR_MESSAGE', { body: 'Unable to save shipping address. Please try again.' }, { root: true })
      return false
    }
  },

  async SAVE_SHIPPING_METHOD ({ commit, rootState }, { method, signUp }) {
    logger.debug('SAVE_SHIPPING_METHOD')

    const body = {
      shipping_method: method,
      gdpr: signUp
    }

    try {
      body.form_key = rootState.cart.formKey
      const { data } = await this.$ecomAPI.quietPost(
        'onepage/saveShippingMethod/',
        qs.stringify(body, { arrayFormat: 'brackets' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.error || typeof (data) === 'string') {
        commit('cart/ADD_ERROR_MESSAGE', { body: data.error || data.message || 'Unable to save shipping method. Please try again.', args: data.message_params || {} }, { root: true })
        return false
      } else {
        logger.debug('SAVE_SHIPPING_METHOD succeeded')
        commit('SET_SHIPPING_METHOD', data.method.code)
        commit('SET_SIGNUP_CHOICE', signUp)
        return data
      }
    } catch (error) {
      logger.debug('SAVE_SHIPPING_METHOD failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      commit('cart/ADD_ERROR_MESSAGE', { body: 'Unable to save shipping method. Please try again.' }, { root: true })
      return false
    }
  },

  async SAVE_BILLING ({ commit, rootState, rootGetters }, address) {
    logger.debug('SAVE_BILLING')

    const body = {
      'shipping-address': 'on',
      billing: {
        address_id: null,
        middlename: null,
        fax: null,
        save_in_address_book: 0,
        ...address
      }
    }

    const regions = rootGetters['store/regions'](address.country)
    if (regions.length > 0 && address.regionId && regions.filter(region => region.region_id === address.regionId).length) {
      body.billing.region_id = address.regionId
    }

    try {
      body.form_key = rootState.cart.formKey
      const { data } = await this.$ecomAPI.quietPost(
        'onepage/saveBilling/',
        qs.stringify(body, { arrayFormat: 'brackets' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.error || typeof (data) === 'string') {
        commit('cart/ADD_ERROR_MESSAGE', { body: data.error || data.message || 'Unable to save billing address. Please try again.', args: data.message_params || {} }, { root: true })
        return false
      } else {
        commit('ADD_ADDRESS', address)
        commit('SET_BILLING_ADDRESS', data.address)
        commit('SET_PAYMENT_METHODS', data.payment_methods)
        logger.debug('SAVE_BILLING succeeded')
        return data
      }
    } catch (error) {
      logger.debug('SAVE_BILLING failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      commit('cart/ADD_ERROR_MESSAGE', { body: 'Unable to save billing address. Please try again.' }, { root: true })
      return false
    }
  },

  async SAVE_PAYMENT_METHOD ({ commit, rootState }, body) {
    logger.debug('SAVE_PAYMENT_METHOD')

    body.form_key = rootState.cart.formKey

    try {
      const { data } = await this.$ecomAPI.quietPost(
        'onepage/savePayment/',
        qs.stringify(body, { arrayFormat: 'brackets' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.error || typeof (data) === 'string') {
        commit('cart/ADD_ERROR_MESSAGE', { body: data.error || data.message || 'Unable to save payment method. Please try again.', args: data.message_params || {} }, { root: true })
        return false
      } else {
        commit('SET_PAYMENT_METHOD', data.payment_method)
        logger.debug('SAVE_PAYMENT_METHOD succeeded')
        return data
      }
    } catch (error) {
      logger.debug('SAVE_PAYMENT_METHOD failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      commit('cart/ADD_ERROR_MESSAGE', { body: 'Unable to save payment method. Please try again.' }, { root: true })
      return false
    }
  },

  async SAVE_ORDER ({ dispatch, commit }, body) {
    logger.debug('SAVE_ORDER')

    try {
      const { data } = await this.$ecomAPI.quietPost(
        'onepage/saveOrder/',
        qs.stringify(body, { arrayFormat: 'brackets' }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      await dispatch('TRACK_ORDER_COMPLETE', { data })
      logger.debug('SAVE_ORDER completed - error not checked')
      return data
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.debug('SAVE_ORDER failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      commit('cart/ADD_ERROR_MESSAGE', { body: 'Unable to submit order. Please try again.' }, { root: true })
      return false
    }
  },

  async GET_CHECKOUT_STATE ({ commit, state }) {
    logger.debug('GET_CHECKOUT_STATE')

    try {
      const { data } = await this.$ecomAPI.get('onepage/')
      
      commit('SET_BILLING_ADDRESS', data.billing_address)
      commit('SET_SHIPPING_ADDRESS', data.shipping_address)
      commit('SET_QUOTE', data.quote)
      commit('SET_ITEMS', data.items)
      commit('SET_COUPONS', data.coupons)
      commit('SET_PAYMENT_METHODS', data.payment_methods)
      commit('SET_PAYMENT_METHOD', data.payment_method)
      commit('SET_SHIPPING_METHODS', data.shipping_methods)
      commit('SET_SHIPPING_METHOD', data.shipping_method_code)
      commit('SET_STEP_STATE', data.step_states)

      if (data.shipping_address) commit('ADD_ADDRESS', data.shipping_address)
      if (data.billing_address) commit('ADD_ADDRESS', data.billing_address)

      logger.debug('GET_CHECKOUT_STATE succeeded')
    } catch (error) {
      logger.debug('GET_CHECKOUT_STATE failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      return false
    }
  },

  async TRACK_ORDER_COMPLETE ({ getters, rootGetters, dispatch }, { data }) {
    dispatch('session/TRACK', { event: 'PURCHASE' }, { root: true })

    this.$track.ecommerce.checkoutCompletion({
      order_number: parseInt(data.orderid),
      total_price: parseFloat(data.amount)
    })

    // Await the async task so all the above have time to complete - this seems sketch.
    await dispatch('session/UPDATE', { checkout_completed: true }, { root: true })
  }
}